import {
  PrtgInstanceStatus,
  PrtgSensor,
  PrtgStatusCounts,
} from "src/questionnaires";

/**
 * Honest health derivation for opsanio appliances.
 *
 * This system has NO time-series telemetry. The only real, live health signal
 * is the *current* PRTG sensor status for an appliance (fetched per-instance via
 * `/prtg/instance/:name`). Everything here is derived from that snapshot - we
 * never fabricate metrics, scores, or trends. An appliance with no PRTG sensors
 * is reported as `unknown`/unmonitored, never as "healthy".
 */

export type HealthStatus = "healthy" | "warning" | "critical" | "unknown";

/** OUI semantic color name for a health status (theme-aware via the stylesheet). */
export const HEALTH_COLOR: Record<HealthStatus, string> = {
  healthy: "success",
  warning: "warning",
  critical: "danger",
  unknown: "subdued",
};

/** OUI icon type for a health status (never communicate status by color alone). */
export const HEALTH_ICON: Record<HealthStatus, string> = {
  healthy: "checkInCircleFilled",
  warning: "alert",
  critical: "crossInACircleFilled",
  unknown: "questionInCircle",
};

export interface DerivedHealth {
  status: HealthStatus;
  /** 0-100 over operational sensors; null when the appliance is unmonitored. */
  score: number | null;
  /** Whether anything is currently reporting up; null when unmonitored. */
  online: boolean | null;
  /** Appliance has at least one PRTG sensor. */
  monitored: boolean;
  /** down + warning + unusual sensors currently open. */
  activeIncidents: number;
  counts: PrtgStatusCounts | null;
  /** ISO timestamp of the PRTG snapshot, when available. */
  lastSync: string | null;
}

export const EMPTY_HEALTH: DerivedHealth = {
  status: "unknown",
  score: null,
  online: null,
  monitored: false,
  activeIncidents: 0,
  counts: null,
  lastSync: null,
};

/**
 * Derive a single appliance's health from its PRTG snapshot.
 *
 * Scoring is over *operational* sensors only (up + down + warning + unusual);
 * `paused` (intentionally off) and `unknown` (no data yet) sensors are excluded
 * so they neither inflate nor deflate the score. Status is the worst signal
 * present: any `down` is critical, any `warning`/`unusual` is warning.
 */
export const deriveHealth = (
  status: PrtgInstanceStatus | null,
): DerivedHealth => {
  if (!status) return EMPTY_HEALTH;
  const counts = status.counts;
  const monitored = (counts?.total ?? 0) > 0;
  if (!counts || !monitored) {
    return { ...EMPTY_HEALTH, lastSync: status.fetchedAt ?? null };
  }

  const operational =
    counts.up + counts.down + counts.warning + counts.unusual;
  const score =
    operational > 0 ? Math.round((100 * counts.up) / operational) : null;

  let healthStatus: HealthStatus;
  if (counts.down > 0) healthStatus = "critical";
  else if (counts.warning > 0 || counts.unusual > 0) healthStatus = "warning";
  else if (counts.up > 0) healthStatus = "healthy";
  else healthStatus = "unknown";

  const online = counts.up + counts.warning + counts.unusual > 0;
  const activeIncidents = counts.down + counts.warning + counts.unusual;

  return {
    status: healthStatus,
    score,
    online,
    monitored: true,
    activeIncidents,
    counts,
    lastSync: status.fetchedAt ?? null,
  };
};

export interface FleetHealth {
  total: number;
  monitored: number;
  /** Average score across monitored appliances; null when none are monitored. */
  averageScore: number | null;
  status: HealthStatus;
  statusCounts: Record<HealthStatus, number>;
  /** Sum of open incidents across the fleet. */
  activeIncidents: number;
  /** Most recent snapshot time across the fleet, ISO; null when none. */
  lastSync: string | null;
}

/** Aggregate per-appliance health into one honest fleet view. */
export const deriveFleetHealth = (items: DerivedHealth[]): FleetHealth => {
  const statusCounts: Record<HealthStatus, number> = {
    healthy: 0,
    warning: 0,
    critical: 0,
    unknown: 0,
  };
  let scoreSum = 0;
  let scored = 0;
  let monitored = 0;
  let activeIncidents = 0;
  let lastSync: string | null = null;

  for (const h of items) {
    statusCounts[h.status] += 1;
    if (h.monitored) monitored += 1;
    if (h.score !== null) {
      scoreSum += h.score;
      scored += 1;
    }
    activeIncidents += h.activeIncidents;
    if (h.lastSync && (!lastSync || h.lastSync > lastSync)) {
      lastSync = h.lastSync;
    }
  }

  const averageScore = scored > 0 ? Math.round(scoreSum / scored) : null;

  // Fleet status reflects the worst *monitored* signal present, never the
  // unmonitored majority.
  let status: HealthStatus;
  if (statusCounts.critical > 0) status = "critical";
  else if (statusCounts.warning > 0) status = "warning";
  else if (statusCounts.healthy > 0) status = "healthy";
  else status = "unknown";

  return {
    total: items.length,
    monitored,
    averageScore,
    status,
    statusCounts,
    activeIncidents,
    lastSync,
  };
};

export type MetricKind = "cpu" | "memory" | "disk" | "network";

const METRIC_PATTERNS: Array<{ kind: MetricKind; re: RegExp }> = [
  { kind: "cpu", re: /\b(cpu|processor|load|auslastung)\b/i },
  { kind: "memory", re: /\b(memory|mem|ram|swap|arbeitsspeicher)\b/i },
  { kind: "disk", re: /\b(disk|volume|storage|partition|free space|speicherplatz|festplatte)\b/i },
  { kind: "network", re: /\b(traffic|network|bandwidth|interface|netzwerk|nic|eth\d*)\b/i },
];

/** Best-effort classification of a PRTG sensor into a metric family by name. */
export const classifyMetric = (sensorName: string): MetricKind | null => {
  for (const { kind, re } of METRIC_PATTERNS) {
    if (re.test(sensorName)) return kind;
  }
  return null;
};

/** OUI icon per metric family. */
export const METRIC_ICON: Record<MetricKind, string> = {
  cpu: "compute",
  memory: "memory",
  disk: "storage",
  network: "globe",
};

/**
 * Pick a representative *current* sensor value per metric family from a flat
 * sensor list. Returns the highest-priority (or first) matching sensor so the
 * flyout can show "current CPU / Memory / Disk" without inventing history.
 */
export const pickMetricSensors = (
  sensors: PrtgSensor[],
): Partial<Record<MetricKind, PrtgSensor>> => {
  const out: Partial<Record<MetricKind, PrtgSensor>> = {};
  for (const s of sensors) {
    const kind = classifyMetric(s.sensor);
    if (!kind) continue;
    const existing = out[kind];
    if (!existing || (s.priority ?? 0) > (existing.priority ?? 0)) {
      out[kind] = s;
    }
  }
  return out;
};

/** All sensors that are currently in an alerting state, worst first. */
export const alertingSensors = (sensors: PrtgSensor[]): PrtgSensor[] => {
  const severity: Record<string, number> = {
    down: 0,
    warning: 1,
    unusual: 2,
  };
  return sensors
    .filter((s) => s.category in severity)
    .sort((a, b) => severity[a.category] - severity[b.category]);
};
